import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Dimensions,
  Platform,
  Image,
  Animated,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { IconButton } from 'react-native-paper';
import SideBarLayout from '../Sidebar/SideBarLayout';
import { parseDimension, getValidIconName } from '../util.style';
import { getActions } from '../../shared';

interface HeaderLayoutProps {
  userName?: string;
  containerStyle?: any;
  screenType?: string;
  screenSize?: { width: number; height: number };
  width?: number;
  height?: number;

  // Live mode props
  attributes?: any;
  style?: any;
  libraryComponentManifest?: any;
  onMenuPress?: () => void;
  onActionIcon1?: () => void;
  onActionIcon2?: () => void;
  onActionIcon3?: () => void;
  onUserPress?: () => void;
  onPress?: (actions: any, options?: any) => void;
  actions?: any;
  id?: string;

  // Mode detection
  isCanvasMode?: boolean;

  [key: string]: any;
}

const HeaderLayout: React.FC<HeaderLayoutProps> = (props) => {
  const {
    attributes,
    // Canvas props
    userName: canvasUserName,
    containerStyle: canvasContainerStyle,
    screenSize,
    width: canvasWidth,
    height: canvasHeight,

    logoText = 'LOGO',
    logoWidth = 100,
    logoHeight = 43,

    // Live props
    onMenuPress,
    onActionIcon1,
    onActionIcon2,
    onActionIcon3,
    onUserPress,
    onPress,
    actions,

    // Mode detection
    isCanvasMode = false,

    ...otherProps
  } = props;
  const { logo, leftMenuPosition } = attributes;
  const { imageUrl, imageLogo } = logo ?? {};
  const { color } = props?.style ?? {};
  const { actionIcon1, actionIcon2, actionIcon3 } = attributes;

  // Handle brand logo URL extraction
  let logoImage = '';
  if (imageLogo?.binding?.url) {
    logoImage = imageLogo.binding.url;
  } else if (imageUrl) {
    logoImage = imageUrl;
  } else if (typeof imageLogo === 'string') {
    // Direct string URL
    logoImage = imageLogo;
  }
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredDropdown, setHoveredDropdown] = useState<string | null>(null);
  const dropdownTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (itemId: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setHoveredDropdown(itemId);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setHoveredDropdown(null);
    }, 150); // Small delay to allow moving to dropdown
  };
  const [screenDimensions, setScreenDimensions] = useState(() =>
    isCanvasMode && screenSize ? screenSize : Dimensions.get('window')
  );
  const [sidebarAnimation] = useState(
    new Animated.Value(Platform.OS === 'web' ? -290 : -300)
  );

  useEffect(() => {
    if (!isCanvasMode) {
      const subscription = Dimensions.addEventListener(
        'change',
        ({ window }) => {
          setScreenDimensions(window);
        }
      );
      return () => subscription?.remove();
    }
  }, [isCanvasMode]);

  // Animate sidebar slide in/out
  useEffect(() => {
    Animated.timing(sidebarAnimation, {
      toValue: isMobileMenuOpen ? 0 : Platform.OS === 'web' ? -290 : -300,
      duration: 300,
      useNativeDriver: false, // Position animations need to use layout thread
    }).start();
  }, [isMobileMenuOpen, sidebarAnimation]);

  const effectiveWidth = isCanvasMode
    ? parseDimension(canvasWidth) || screenSize?.width || 1200
    : screenDimensions.width;

  const widthNumber =
    typeof effectiveWidth === 'number' ? effectiveWidth : 1200;
  const isDesktop = widthNumber >= 1200;
  const isTablet = widthNumber >= 768 && widthNumber < 1200;
  const isMobile = widthNumber < 768;

  // Mock data for canvas mode
  const mockData = {
    userName: 'ユーザー名',
    menuItems: ['メニュー1', 'メニュー2', 'メニュー3'],
  };

  const getMenuItems = () => {
    const menuListItems = attributes?.menuList?.items;
    if (
      menuListItems &&
      Array.isArray(menuListItems) &&
      menuListItems.length > 0
    ) {
      return menuListItems;
    }
    // Fallback to default display
    return mockData.menuItems.map((item, index) => ({
      id: `default-${index}`,
      text: item,
      type: 'item',
    }));
  };

  const menuItems = getMenuItems();

  // Transform Header menu items format to Sidebar format (including children)
  const transformedMenuItems = menuItems.map((item) => ({
    ...item,
    label: item.text || item.label, // Map text to label for Sidebar compatibility
    children: item.children
      ? item.children.map((child: any) => ({
          ...child,
          label: child.text || child.label, // Map text to label for children too
        }))
      : undefined,
  }));

  // Use canvas data or live data
  const userName = isCanvasMode
    ? canvasUserName || mockData.userName
    : otherProps.userName || mockData.userName;

  const handleActionIcon1Press = () => {
    if (isCanvasMode) return;

    if (actionIcon1?.action && onPress) {
      const actionProps = {
        ...props,
        attributes: {
          ...props.attributes,
          action: actionIcon1.action,
        },
      };
      const actionData = getActions(actionProps, 'action');
      onPress(actionData, {});
    } else if (onActionIcon1) {
      onActionIcon1();
    }
  };

  const handleActionIcon2Press = () => {
    if (isCanvasMode) return;

    if (actionIcon2?.action && onPress) {
      const actionProps = {
        ...props,
        attributes: {
          ...props.attributes,
          action: actionIcon2.action,
        },
      };
      const actionData = getActions(actionProps, 'action');
      onPress(actionData, {});
    } else if (onActionIcon2) {
      onActionIcon2();
    }
  };

  const handleActionIcon3Press = () => {
    if (isCanvasMode) return;

    if (actionIcon3?.action && onPress) {
      const actionProps = {
        ...props,
        attributes: {
          ...props.attributes,
          action: actionIcon3.action,
        },
      };
      const actionData = getActions(actionProps, 'action');
      onPress(actionData, {});
    } else if (onActionIcon3) {
      onActionIcon3();
    }
  };

  const handleMenuItemPress = (item: any) => {
    if (isCanvasMode) return;

    if (item.action && onPress) {
      // Create a temporary props object with the specific menu item's action
      const actionProps = {
        ...props,
        attributes: {
          ...props.attributes,
          action: item.action,
        },
      };
      const actionData = getActions(actionProps, 'action');
      onPress(actionData, {});
    } else if (onMenuPress) {
      onMenuPress();
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (!isCanvasMode) {
      onMenuPress?.();
    }
  };

  // Responsive heights and padding according to specification
  const getResponsiveHeight = () => {
    if (isDesktop) return 60;
    if (isTablet) return 56;
    return 52;
  };

  const getResponsiveContainerPadding = () => {
    if (isDesktop) return 48;
    if (isTablet) return 24;
    return 20;
  };

  // Safe area padding for native (wrapper container)
  const safeAreaPaddingTop = canvasContainerStyle?.paddingTop || 0;

  // Container styling
  const headerStyle = {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    backgroundColor: isCanvasMode
      ? canvasContainerStyle?.backgroundColor || '#f5f5f5'
      : canvasContainerStyle?.backgroundColor,
    height: isCanvasMode
      ? parseDimension(canvasContainerStyle?.height) || getResponsiveHeight()
      : getResponsiveHeight(),
    paddingHorizontal: getResponsiveContainerPadding(),
    zIndex: isCanvasMode ? canvasContainerStyle?.zIndex || 1000 : 1000,
    width: isCanvasMode
      ? parseDimension(canvasContainerStyle?.width) || effectiveWidth
      : '100%',
  };

  const logoContainerStyle = {
    height: 43,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
  };

  const logoTextStyle = {
    fontSize:
      props?.style?.fontSize ||
      getResponsiveLogoSize(
        typeof effectiveWidth === 'number' ? effectiveWidth : 1200
      ),
    fontWeight: 'bold' as const,
    fontFamily: props?.style?.fontFamily,
    color: color,
  };

  const logoImageStyle = {
    width: Math.max(logoWidth || 100, 100),
    height: Math.min(logoHeight || 43, 43),
    resizeMode: 'contain' as const,
  };

  const renderLogo = () => {
    if (logoImage) {
      return (
        <Image
          source={{ uri: logoImage }}
          style={logoImageStyle}
          testID="header-logo-image"
        />
      );
    }
    return (
      <Text style={logoTextStyle} testID="header-logo-text">
        {logoText}
      </Text>
    );
  };

  const renderDesktopNav = () => {
    const desktopNavStyle = {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      flex: 1,
      justifyContent: 'space-between' as const,
      marginLeft: 48, // Space between logo and menu items
    };

    const navMenuStyle = {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      ...(Platform.OS === 'web' && { gap: 24 }),
    };

    const menuItemStyle = {
      paddingVertical: 8,
      paddingHorizontal: 2,
      borderRadius: 6,
      marginHorizontal: Platform.OS === 'web' ? 0 : 12,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
    };

    const menuTextStyle = {
      color: color,
      fontWeight: '500' as const,
      fontSize: props?.style?.fontSize || (isDesktop ? 16 : 15),
      fontFamily: props?.style?.fontFamily,
    };

    const userActionsStyle = {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      ...(Platform.OS === 'web' && { gap: 16 }),
    };

    return (
      <View style={desktopNavStyle}>
        <View style={navMenuStyle}>
          {menuItems.map((item, index) => (
            <View key={item.id || index} style={{ position: 'relative' }}>
              <TouchableOpacity
                style={menuItemStyle}
                onPress={() => handleMenuItemPress(item)}
                disabled={isCanvasMode}
                {...(Platform.OS === 'web' &&
                  item.type === 'group' && {
                    onMouseEnter: () =>
                      handleMouseEnter(item.id || `item-${index}`),
                    onMouseLeave: handleMouseLeave,
                  })}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={menuTextStyle}>
                    {item.text || `メニュー${index + 1}`}
                  </Text>
                  {item.type === 'group' && (
                    <Icon
                      name="keyboard-arrow-down"
                      size={props?.style?.fontSize ?? 15}
                      color={color}
                      style={{ marginLeft: 4 }}
                    />
                  )}
                </View>
              </TouchableOpacity>

              {/* Dropdown Menu */}
              {item.type === 'group' &&
                item.children &&
                hoveredDropdown === (item.id || `item-${index}`) &&
                Platform.OS === 'web' && (
                  <View
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      backgroundColor:
                        canvasContainerStyle?.backgroundColor || '#ffffff',
                      minWidth: 200,
                      borderRadius: 4,
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.1,
                      shadowRadius: 4,
                      elevation: 4,
                      zIndex: 1000,
                      paddingVertical: 8,
                      marginTop: 4,
                    }}
                    {...(Platform.OS === 'web' && {
                      onMouseEnter: () =>
                        handleMouseEnter(item.id || `item-${index}`),
                      onMouseLeave: handleMouseLeave,
                    })}
                  >
                    {item.children.map((child: any, childIndex: number) => (
                      <TouchableOpacity
                        key={child.id || childIndex}
                        style={{
                          paddingVertical: 12,
                          paddingHorizontal: 16,
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                        onPress={() => handleMenuItemPress(child)}
                        disabled={isCanvasMode}
                        {...(Platform.OS === 'web' && {
                          onMouseEnter: (e: any) => {
                            e.currentTarget.style.backgroundColor =
                              canvasContainerStyle?.backgroundColor
                                ? hexToRgba(
                                    canvasContainerStyle?.backgroundColor,
                                    0.2
                                  )
                                : '#ffffff';
                          },
                          onMouseLeave: (e: any) => {
                            e.currentTarget.style.backgroundColor =
                              'transparent';
                          },
                        })}
                      >
                        {child.icon && (
                          <Icon
                            name={getValidIconName(child.icon, isCanvasMode)}
                            size={16}
                            color={color || '#333'}
                            style={{ marginRight: 8 }}
                          />
                        )}
                        <Text
                          style={{
                            color: color || '#333',
                            fontSize: 14,
                            fontFamily: props?.style?.fontFamily,
                          }}
                        >
                          {child.text || child.label}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
            </View>
          ))}
        </View>

        <View style={userActionsStyle}>
          {actionIcon1 && actionIcon1.enabled && (
            <TouchableOpacity
              style={getIconButtonStyle()}
              onPress={handleActionIcon1Press}
              disabled={isCanvasMode}
            >
              <IconButton
                size={props?.style?.fontSize ?? 15}
                icon={actionIcon1.icon}
                color={color}
                style={{ borderRadius: 2 }}
              />
            </TouchableOpacity>
          )}

          {actionIcon2 && actionIcon2.enabled && (
            <TouchableOpacity
              style={getIconButtonStyle()}
              onPress={handleActionIcon2Press}
              disabled={isCanvasMode}
            >
              <IconButton
                size={props?.style?.fontSize ?? 15}
                icon={actionIcon2.icon}
                color={color}
                style={{ borderRadius: 2 }}
              />
            </TouchableOpacity>
          )}

          {isDesktop && actionIcon3 && actionIcon3.enabled && (
            <TouchableOpacity
              style={getIconButtonStyle()}
              onPress={handleActionIcon3Press}
              disabled={isCanvasMode}
            >
              <IconButton
                size={props?.style?.fontSize ?? 15}
                icon={actionIcon3.icon}
                color={color}
                style={{ borderRadius: 2 }}
              />
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={getUserProfileStyle(effectiveWidth)}
            onPress={isCanvasMode ? undefined : onUserPress}
            disabled={isCanvasMode}
          >
            <View style={getAvatarStyle(effectiveWidth)}>
              <IconButton
                size={props?.style?.fontSize ?? 15}
                icon={'account-circle'}
                color={color}
                style={{ borderRadius: 2 }}
              />
            </View>
            <Text style={getUserNameStyle(effectiveWidth, color, props?.style)}>
              {userName}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderTabletNav = () => {
    const tabletNavStyle = {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      flex: 1,
      justifyContent: 'space-between' as const,
      marginLeft: 32, // Space between logo and menu items
    };

    const navMenuStyle = {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      ...(Platform.OS === 'web' && { gap: 24 }),
    };

    const menuItemStyle = {
      paddingVertical: 8,
      paddingHorizontal: 2,
      borderRadius: 6,
      marginHorizontal: Platform.OS === 'web' ? 0 : 2,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
    };

    const userActionsStyle = {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      ...(Platform.OS === 'web' && { gap: 1 }),
    };

    return (
      <View style={tabletNavStyle}>
        <View style={navMenuStyle}>
          {menuItems.map((item, index) => (
            <View key={item.id || index} style={{ position: 'relative' }}>
              <TouchableOpacity
                style={menuItemStyle}
                onPress={() => handleMenuItemPress(item)}
                disabled={isCanvasMode}
                {...(Platform.OS === 'web' &&
                  item.type === 'group' && {
                    onMouseEnter: () =>
                      handleMouseEnter(item.id || `item-${index}`),
                    onMouseLeave: handleMouseLeave,
                  })}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text
                    style={{
                      color: color || '#333',
                      fontWeight: '500',
                      fontSize: props?.style?.fontSize || 15,
                      fontFamily: props?.style?.fontFamily,
                    }}
                  >
                    {item.text || `メニュー${index + 1}`}
                  </Text>
                  {item.type === 'group' && (
                    <Icon
                      name="keyboard-arrow-down"
                      size={props?.style?.fontSize ?? 15}
                      color={color}
                      style={{ marginLeft: 4 }}
                    />
                  )}
                </View>
              </TouchableOpacity>

              {/* Dropdown Menu */}
              {item.type === 'group' &&
                item.children &&
                hoveredDropdown === (item.id || `item-${index}`) &&
                Platform.OS === 'web' && (
                  <View
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      backgroundColor:
                        canvasContainerStyle?.backgroundColor || '#ffffff',
                      minWidth: 200,
                      borderRadius: 4,
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.1,
                      shadowRadius: 4,
                      elevation: 4,
                      zIndex: 1000,
                      paddingVertical: 8,
                      marginTop: 4,
                    }}
                    {...(Platform.OS === 'web' && {
                      onMouseEnter: () =>
                        handleMouseEnter(item.id || `item-${index}`),
                      onMouseLeave: handleMouseLeave,
                    })}
                  >
                    {item.children.map((child: any, childIndex: number) => (
                      <TouchableOpacity
                        key={child.id || childIndex}
                        style={{
                          paddingVertical: 12,
                          paddingHorizontal: 16,
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                        onPress={() => handleMenuItemPress(child)}
                        disabled={isCanvasMode}
                        {...(Platform.OS === 'web' && {
                          onMouseEnter: (e: any) => {
                            e.currentTarget.style.backgroundColor =
                              canvasContainerStyle?.backgroundColor ||
                              '#ffffff';
                          },
                          onMouseLeave: (e: any) => {
                            e.currentTarget.style.backgroundColor =
                              'transparent';
                          },
                        })}
                      >
                        {child.icon && (
                          <Icon
                            name={getValidIconName(child.icon, isCanvasMode)}
                            size={16}
                            color={color || '#333'}
                            style={{ marginRight: 8 }}
                          />
                        )}
                        <Text
                          style={{
                            color: color || '#333',
                            fontSize: 14,
                            fontFamily: props?.style?.fontFamily,
                          }}
                        >
                          {child.text || child.label}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
            </View>
          ))}
        </View>

        <View style={userActionsStyle}>
          {actionIcon1 && actionIcon1.enabled && (
            <TouchableOpacity
              style={getIconButtonStyle()}
              onPress={handleActionIcon1Press}
              disabled={isCanvasMode}
            >
              <IconButton
                size={props?.style?.fontSize ?? 15}
                icon={actionIcon1.icon}
                color={color}
                style={{ borderRadius: 2 }}
              />
            </TouchableOpacity>
          )}

          {actionIcon2 && actionIcon2.enabled && (
            <TouchableOpacity
              style={getIconButtonStyle()}
              onPress={handleActionIcon2Press}
              disabled={isCanvasMode}
            >
              <IconButton
                size={props?.style?.fontSize ?? 15}
                icon={actionIcon2.icon}
                color={color}
                style={{ borderRadius: 2 }}
              />
            </TouchableOpacity>
          )}

          {actionIcon3 && actionIcon3.enabled && (
            <TouchableOpacity
              style={getIconButtonStyle()}
              onPress={handleActionIcon3Press}
              disabled={isCanvasMode}
            >
              <IconButton
                size={props?.style?.fontSize ?? 15}
                icon={actionIcon3.icon}
                color={color}
                style={{ borderRadius: 2 }}
              />
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={getUserProfileStyle(effectiveWidth)}
            onPress={isCanvasMode ? undefined : onUserPress}
            disabled={isCanvasMode}
          >
            <View style={getAvatarStyle(effectiveWidth)}>
              <IconButton
                size={props?.style?.fontSize ?? 15}
                icon={'account-circle'}
                color={color}
                style={{ borderRadius: 2 }}
              />
            </View>
            <Text style={getUserNameStyle(effectiveWidth, color, props?.style)}>
              {userName}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderMobileNav = () => {
    const mobileActionsStyle = {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      ...(Platform.OS === 'web' && { gap: 0 }),
    };

    const shouldShowMenuOnLeft = leftMenuPosition?.enabled !== false;

    const actionIconsSection = (
      <>
        {actionIcon1 && actionIcon1.enabled && (
          <TouchableOpacity
            style={getIconButtonStyle()}
            onPress={isCanvasMode ? undefined : onActionIcon1}
            disabled={isCanvasMode}
          >
            <IconButton
              size={props?.style?.fontSize ?? 15}
              icon={actionIcon1.icon}
              color={color}
              style={{ borderRadius: 2 }}
            />
          </TouchableOpacity>
        )}

        {actionIcon2 && actionIcon2.enabled && (
          <TouchableOpacity
            style={getIconButtonStyle()}
            onPress={isCanvasMode ? undefined : onActionIcon2}
            disabled={isCanvasMode}
          >
            <IconButton
              size={props?.style?.fontSize ?? 15}
              icon={actionIcon2.icon}
              color={color}
              style={{ borderRadius: 2 }}
            />
          </TouchableOpacity>
        )}
      </>
    );

    const menuButton = (
      <TouchableOpacity
        style={getIconButtonStyle()}
        onPress={toggleMobileMenu}
        disabled={isCanvasMode}
      >
        <Icon
          name="menu"
          size={props?.style?.fontSize ?? 15}
          color={color ?? '#333'}
        />
      </TouchableOpacity>
    );

    if (shouldShowMenuOnLeft) {
      return (
        <View style={mobileActionsStyle}>
          {menuButton}
          {actionIconsSection}
        </View>
      );
    }

    return (
      <View style={mobileActionsStyle}>
        {actionIconsSection}
        {menuButton}
      </View>
    );
  };

  const getNativeID = () => {
    if (isDesktop) return 'DesktopCanvasView';
    if (isTablet) return 'TabletCanvasView';
    return 'MobileCanvasView';
  };

  const shouldShowMenuOnLeft = leftMenuPosition?.enabled !== false;

  return (
    <>
      {Platform.OS !== 'web' && safeAreaPaddingTop > 0 ? (
        // Native: Wrap with safe area padding
        <View
          style={{
            paddingTop: safeAreaPaddingTop,
            backgroundColor: headerStyle.backgroundColor,
          }}
        >
          <View style={headerStyle} nativeID={getNativeID()}>
            {/* Mobile layout with conditional positioning */}
            {isMobile && shouldShowMenuOnLeft ? (
              <>
                {/* Left Section: Hamburger menu and logo together */}
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <TouchableOpacity
                    style={getIconButtonStyle()}
                    onPress={toggleMobileMenu}
                    disabled={isCanvasMode}
                  >
                    <Icon
                      name="menu"
                      size={props?.style?.fontSize ?? 15}
                      color={color ?? '#333'}
                    />
                  </TouchableOpacity>
                  <View style={logoContainerStyle}>{renderLogo()}</View>
                </View>
                {/* Right Section: Action icons only */}
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  {actionIcon1 && actionIcon1.enabled && (
                    <TouchableOpacity
                      style={getIconButtonStyle()}
                      onPress={isCanvasMode ? undefined : onActionIcon1}
                      disabled={isCanvasMode}
                    >
                      <IconButton
                        size={props?.style?.fontSize ?? 15}
                        icon={actionIcon1.icon}
                        color={color}
                        style={{ borderRadius: 2 }}
                      />
                    </TouchableOpacity>
                  )}
                  {actionIcon2 && actionIcon2.enabled && (
                    <TouchableOpacity
                      style={getIconButtonStyle()}
                      onPress={isCanvasMode ? undefined : onActionIcon2}
                      disabled={isCanvasMode}
                    >
                      <IconButton
                        size={props?.style?.fontSize ?? 15}
                        icon={actionIcon2.icon}
                        color={color}
                        style={{ borderRadius: 2 }}
                      />
                    </TouchableOpacity>
                  )}
                </View>
              </>
            ) : isMobile ? (
              <>
                {/* Left Section: Logo */}
                <View style={logoContainerStyle}>{renderLogo()}</View>
                {/* Right Section: Mobile Menu */}
                {renderMobileNav()}
              </>
            ) : (
              <>
                {/* Desktop/Tablet Layout */}
                {/* Left Section: Logo */}
                <View style={logoContainerStyle}>{renderLogo()}</View>
                {/* Center + Right sections */}
                {isDesktop ? renderDesktopNav() : renderTabletNav()}
              </>
            )}
          </View>
        </View>
      ) : (
        // Web: Render without wrapper
        <View style={headerStyle} nativeID={getNativeID()}>
          {/* Mobile layout with conditional positioning */}
          {isMobile && shouldShowMenuOnLeft ? (
            <>
              {/* Left Section: Hamburger menu and logo together */}
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <TouchableOpacity
                  style={getIconButtonStyle()}
                  onPress={toggleMobileMenu}
                  disabled={isCanvasMode}
                >
                  <Icon
                    name="menu"
                    size={props?.style?.fontSize ?? 15}
                    color={color ?? '#333'}
                  />
                </TouchableOpacity>
                <View style={logoContainerStyle}>{renderLogo()}</View>
              </View>
              {/* Right Section: Action icons only */}
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                {actionIcon1 && actionIcon1.enabled && (
                  <TouchableOpacity
                    style={getIconButtonStyle()}
                    onPress={isCanvasMode ? undefined : onActionIcon1}
                    disabled={isCanvasMode}
                  >
                    <IconButton
                      size={props?.style?.fontSize ?? 15}
                      icon={actionIcon1.icon}
                      color={color}
                      style={{ borderRadius: 2 }}
                    />
                  </TouchableOpacity>
                )}
                {actionIcon2 && actionIcon2.enabled && (
                  <TouchableOpacity
                    style={getIconButtonStyle()}
                    onPress={isCanvasMode ? undefined : onActionIcon2}
                    disabled={isCanvasMode}
                  >
                    <IconButton
                      size={props?.style?.fontSize ?? 15}
                      icon={actionIcon2.icon}
                      color={color}
                      style={{ borderRadius: 2 }}
                    />
                  </TouchableOpacity>
                )}
              </View>
            </>
          ) : isMobile ? (
            <>
              {/* Left Section: Logo */}
              <View style={logoContainerStyle}>{renderLogo()}</View>
              {/* Right Section: Mobile Menu */}
              {renderMobileNav()}
            </>
          ) : (
            <>
              {/* Desktop/Tablet Layout */}
              {/* Left Section: Logo */}
              <View style={logoContainerStyle}>{renderLogo()}</View>
              {/* Center + Right sections */}
              {isDesktop ? renderDesktopNav() : renderTabletNav()}
            </>
          )}
        </View>
      )}

      {/* Mobile Sidebar - only show in live mode, mobile view, and when menu is open */}
      {!isCanvasMode && isMobile && isMobileMenuOpen && (
        <>
          {/* Overlay backdrop - covers entire screen including status bar */}
          <TouchableOpacity
            style={{
              position: (Platform.OS === 'web' ? 'fixed' : 'absolute') as any,
              top: Platform.OS === 'web' ? 0 : -safeAreaPaddingTop, // Start from top of screen on native
              left: 0,
              right: 0,
              bottom: 0,
              height:
                Platform.OS === 'web'
                  ? '100vh'
                  : screenDimensions.height + safeAreaPaddingTop,
              width: Platform.OS === 'web' ? '100vw' : screenDimensions.width,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9998,
            }}
            onPress={() => setIsMobileMenuOpen(false)}
            activeOpacity={1}
          />

          {/* Animated Sidebar - with safe area padding */}
          <Animated.View
            style={{
              position: (Platform.OS === 'web' ? 'fixed' : 'absolute') as any,
              top: Platform.OS === 'web' ? 0 : -safeAreaPaddingTop, // Start from top of screen
              left: sidebarAnimation,
              bottom: 0,
              width: 280,
              height:
                Platform.OS === 'web'
                  ? '100vh'
                  : screenDimensions.height + safeAreaPaddingTop,
              backgroundColor:
                canvasContainerStyle?.backgroundColor || '#f5f5f5',
              zIndex: 9999,
              elevation: 10, // Android shadow
              shadowColor: '#000', // iOS shadow
              shadowOffset: { width: 2, height: 0 },
              shadowOpacity: 0.3,
              shadowRadius: 8,
            }}
          >
            <SideBarLayout
              attributes={{
                logo: {
                  ...logo,
                  enabled: true,
                },
              }}
              backgroundColor={
                isCanvasMode
                  ? canvasContainerStyle?.backgroundColor || '#f5f5f5'
                  : canvasContainerStyle?.backgroundColor || '#f5f5f5'
              }
              textColor={color ?? '#333333'}
              activeColor={color ? hexToRgba(color, 0.2) : '#3498db'}
              hoverColor={color ? hexToRgba(color, 0.2) : '#e9ecef'}
              isCanvasMode={false} // Allow interactions in mobile sidebar
              userName={userName}
              userEmail="mail@sample.com"
              logoText={logoText}
              logoImage={logoImage}
              logoHeight={40}
              position="left"
              width={280}
              height={screenDimensions.height} // Sidebar content height (excluding safe area)
              collapsible={false}
              collapsed={false}
              showLogo={true}
              logoPosition="top"
              showUserProfile={true}
              userProfilePosition="top"
              menuItems={transformedMenuItems}
              screenSize={{ width: 1200, height: screenDimensions.height }} // Force desktop screen size
              containerStyle={{ width: 280, height: screenDimensions.height }} // Force desktop container
              safeAreaPaddingTop={
                Platform.OS === 'web' ? 0 : safeAreaPaddingTop
              }
              onMenuItemPress={(item) => {
                setIsMobileMenuOpen(false);
                handleMenuItemPress(item);
              }}
            />
          </Animated.View>
        </>
      )}
    </>
  );
};

// Convert HEX (#RRGGBB or #RGB) + opacity into RGBA
const hexToRgba = (hex: any, opacity = 1) => {
  let r = 0,
    g = 0,
    b = 0;

  // 3-digit hex (#RGB)
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  }
  // 6-digit hex (#RRGGBB)
  else if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16);
    g = parseInt(hex.substring(3, 5), 16);
    b = parseInt(hex.substring(5, 7), 16);
  }

  return `rgba(${r},${g},${b},${opacity})`;
};

// Helper functions for inline styles
const getResponsiveLogoSize = (width: number): number => {
  if (width >= 1200) return 28;
  if (width >= 768) return 24;
  return 20;
};

const getIconButtonStyle = () => ({
  padding: 2,
  borderRadius: 20,
  minWidth: 44,
  minHeight: 44,
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  marginHorizontal: Platform.OS === 'web' ? 0 : 8,
  opacity: 0.8,
});

const getAvatarStyle = (width: number | string) => {
  const size = (typeof width === 'number' ? width : 1024) >= 1024 ? 36 : 32;
  return {
    width: size,
    height: size,
    borderRadius: size / 2,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  };
};

const getUserProfileStyle = (width: number | string) => {
  const baseStyle = {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    marginLeft: Platform.OS === 'web' ? 0 : 8,
  };

  if ((typeof width === 'number' ? width : 1024) >= 1024) {
    return {
      ...baseStyle,
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 24,
      ...(Platform.OS === 'web' && { gap: 12 }),
    };
  }
  if ((typeof width === 'number' ? width : 768) >= 768) {
    return {
      ...baseStyle,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 20,
      ...(Platform.OS === 'web' && { gap: 8 }),
    };
  }
  return {
    ...baseStyle,
    padding: 4,
  };
};

const getUserNameStyle = (
  width: number | string,
  color: string,
  style?: any
) => ({
  color: color ?? '#333',
  fontWeight: '500' as const,
  fontSize:
    style?.fontSize ||
    ((typeof width === 'number' ? width : 1024) >= 1024 ? 15 : 14),
  fontFamily: style?.fontFamily,
  marginLeft: Platform.OS === 'web' ? 0 : 8,
});

export default HeaderLayout;
